import moment from 'moment';
import Const from '../common/Const';

export {
  customDataChart,
  getMinMaxData,
  executeRealtimeData,
  getTickValues,
  getYExtentsChart,
  getColumnSizeInBarChart,
  yExtentsCharts,
  tickIntervalFunction,
  getRoundedTickValues,
  getMinMax,
  getXAxisFormatAndTicks,
  getTickFormat,
};

function getMinMaxData(array, valueAttributeName, referenceIndex) {
  if (array.length === 0) {
    return { max: 0, min: 0 };
  }

  const tmpVal = referenceIndex
    ? array[array.length - 1][referenceIndex] // get last reference Price/value of index/ticker for latest info
    : array[0][valueAttributeName];

  let max = tmpVal;
  let min = tmpVal;

  array.map(item => {
    let value = item[valueAttributeName];
    if (value > max) {
      max = value;
    }
    if (value < min) {
      min = value;
    }
  });

  return { max, min };
}

function ignoreDataAfterDate(data, hour) {
  const result = [];
  data.some(item => {
    const tradingDateInHour = moment(item.tradingDate)
      .hours(hour)
      .minutes(0)
      .seconds(0);
    if (
      moment(item.tradingDate)
        .startOf('minute')
        .diff(tradingDateInHour) > 0
    ) {
      return true;
    }
    result.push(item);
  });
  return result;
}

function formatPoint(currentPoint, type, tradingDate) {
  switch (type) {
    case 'index':
      return {
        ...currentPoint,
        tradingDate,
        comGroupCode: currentPoint.comGroupCode,
        indexValue: currentPoint.indexValue,
        totalMatchValue: currentPoint.totalMatchValue,
        totalMatchVolume: currentPoint.totalMatchVolume,
        referenceIndex: currentPoint.referenceIndex,
      };
    case 'ticker':
      return {
        ...currentPoint,
        tradingDate,
        ticker: currentPoint.ticker,
        matchPrice: currentPoint.matchPrice,
        matchVolume: currentPoint.matchVolume,
        referencePrice: currentPoint.referencePrice,
      };
    case 'foreign':
      return {
        ...currentPoint,
        tradingDate,
        foreignBuyValueMatched: currentPoint.foreignBuyValueMatched,
        foreignSellValueMatched: currentPoint.foreignSellValueMatched,
      };
    default:
      return {};
  }
}

function getMapKey(date) {
  return `${date.getHours()}_${date.getMinutes()}`;
}

function getHashMapData(data) {
  const hashMapData = {};
  const startTime = new Date();
  startTime.setHours(9, 0, 0);
  hashMapData[getMapKey(startTime)] = {
    ...data[0],
    tradingDate: startTime.toString(),
  };
  data.forEach(item => {
    const date = moment(item.tradingDate).toDate();
    const key = getMapKey(date);
    hashMapData[key] = item;
  });

  return hashMapData;
}

function getEmptyPoint(prevPoint, type) {
  switch (type) {
    case 'index':
      return {
        comGroupCode: prevPoint.comGroupCode,
        referenceIndex: prevPoint.referenceIndex,
      };
    case 'ticker':
      return {
        ticker: prevPoint.ticker,
        referencePrice: prevPoint.referencePrice,
      };
    case 'foreign':
      return {
        foreignBuyValue: null,
        foreignSellValue: null,
      };
    default:
      return {};
  }
}

function customDataChart(data, timeRange, type = 'index') {
  if (timeRange !== Const.timeRange['1D'] || !data.length) {
    return data;
  }
  const startTime = new Date();
  const endTime = moment(data[data.length - 1].tradingDate).toDate();
  endTime.setDate(startTime.getDate());
  startTime.setHours(9, 0, 0);
  const fifteenHour = new Date();
  fifteenHour.setHours(15, 5, 0);
  const formattedData = [];
  const hashMapData = getHashMapData(data);
  while (startTime.getTime() <= fifteenHour.getTime()) {
    let prevPoint = formattedData[formattedData.length - 1] || {};
    if (startTime.getTime() > endTime.getTime()) {
      prevPoint = getEmptyPoint(prevPoint, type);
    }
    const key = getMapKey(startTime);
    const hours = startTime.getHours();
    const minutes = startTime.getMinutes();

    const point = hashMapData[key]
      ? formatPoint(hashMapData[key], type, startTime.toString())
      : formatPoint(
          { ...prevPoint, matchVolume: 0 },
          type,
          startTime.toString(),
        );
    if (!(hours === 15 && minutes > 0 && !hashMapData[key])) {
      formattedData.push(point);
    }
    startTime.setMilliseconds(startTime.getMilliseconds() + 60 * 1000);
  }

  return formattedData;
}

function executeRealtimeData(data, listDataChart) {
  const newTradingDate = moment(data.tradingDate).startOf('minute');

  const lengthListDataChart = listDataChart.length;

  if (lengthListDataChart > 0) {
    const currentNode = { ...listDataChart[lengthListDataChart - 1] };
    const currentTradingDate = moment(currentNode.tradingDate).startOf(
      'minute',
    );

    const diffTradingDate = newTradingDate.diff(currentTradingDate, 'minute');
    if (diffTradingDate === 0) {
      listDataChart.splice(lengthListDataChart - 1, 1);
    }
  }

  listDataChart.push(data);
  return listDataChart;
}

function getTickValues(length, count) {
  let result = [];
  const min = Math.min(length, count);
  const step = Math.floor((length - 1) / (min - 1));

  for (let index = 0; index < min - 1; index++) {
    result.push(step * index);
  }

  result.push(length - 1);

  return result;
}

const getMaxData = data => {
  let max = 0;
  data.forEach(value => {
    max = parseFloat(value) > max ? parseFloat(value) : max;
  });
  return max;
};

const getMinData = data => {
  let min = 1000000;
  data.forEach(value => {
    min = parseFloat(value) < min ? parseFloat(value) : min;
  });
  return min;
};

const formatMinMaxWithZero = (max, min, newMax, newMin, isWithZero) => {
  return isWithZero
    ? {
        max: max < newMax ? newMax : max,
        min: min > newMin ? newMin : min,
      }
    : {
        min: newMin,
        max: newMax,
      };
};

const getMinMax = (
  data,
  arrKey,
  tickInterval,
  isStackedBar = false,
  isWithZero = true,
) => {
  let max = 0,
    min = 0;
  if (isStackedBar) {
    data.forEach(element => {
      let newMax = 0;
      let newMin = 0;
      arrKey.forEach(key => {
        newMax += element[key] < 0 ? 0 : element[key];
        newMin += element[key] > 0 ? 0 : element[key];
      });
      const maxMinData = formatMinMaxWithZero(
        max,
        min,
        newMax,
        newMin,
        isWithZero,
      );
      max = maxMinData.max;
      min = maxMinData.min;
    });
  } else {
    arrKey.forEach(element => {
      let arrData = data.map(a => a[element]);
      let newMax = getMaxData(arrData);
      let newMin = getMinData(arrData);
      const maxMinData = formatMinMaxWithZero(
        max,
        min,
        newMax,
        newMin,
        isWithZero,
      );
      max = maxMinData.max;
      min = maxMinData.min;
    });
  }
  return [Math.floor(min), max];
};

const yExtentsCharts = (
  data,
  arrKey,
  tickInterval,
  isStackedBar = false,
  isWithZero = true,
) => {
  const minMaxArr = getMinMax(
    data,
    arrKey,
    tickInterval,
    isStackedBar,
    isWithZero,
  );
  return getYExtentsChart(minMaxArr[0], minMaxArr[1], tickInterval);
};

function getYExtentsChart(yMin, yMax, tickInterval, format = val => val) {
  const step = Math.abs(Math.ceil((yMax - yMin) / tickInterval));
  const newMin = Math.floor(yMin / step) * step;
  const newMax = Math.ceil(yMax / step) * step;

  return [format(newMin), format(newMax)];
}

function tickIntervalFunction(min, max, tickInterval) {
  if (min < 0) {
    tickInterval += 1;
  }
  const step = Math.abs((max - min) / tickInterval);
  let i = -1,
    n = Math.max(0, Math.ceil(Math.abs(max - min) / step)) | 0,
    range = new Array(n);
  while (++i <= n) {
    range[i] = min + i * step;
  }
  return range;
}

function getColumnSizeInBarChart(width, count = 1, size = 500) {
  return width > size ? 30 * count : 16 * count;
}

const roundMinValue = value => {
  if (value < 0) {
    if (value > -10) {
      return Math.floor(value);
    }

    if (value > -1000) {
      return Math.floor(value / 10) * 10;
    }

    return Math.floor(value / 100) * 100;
  }

  if (value < 10) {
    return Math.floor(value);
  }

  if (value < 1000) {
    return Math.floor(value / 10) * 10;
  }

  return Math.floor(value / 100) * 100;
};

const roundMaxValue = value => {
  if (value < 0) {
    if (value > -10) {
      return Math.ceil(value);
    }

    if (value > -1000) {
      return Math.ceil(value / 10) * 10;
    }

    return Math.ceil(value / 100) * 100;
  }

  if (value < 10) {
    return Math.ceil(value);
  }

  if (value < 1000) {
    return Math.ceil(value / 10) * 10;
  }

  return Math.ceil(value / 100) * 100;
};

const roundOffset = value => {
  if (value < 10) {
    return Math.ceil(value);
  }

  if (value < 100) {
    return Math.ceil(value / 10) * 10;
  }

  return Math.ceil(value / 100) * 100;
};

const getRoundedTickValues = ([min, max], ticks = 6) => {
  if (max < ticks && max > 0) {
    const tickValues = [];
    const offset = (max - min) / (ticks - 1);
    for (let i = 0; i < ticks; i++) {
      tickValues.push(min + i * offset);
    }
    return tickValues;
  }
  const roundedMax = roundMaxValue(max);
  const roundedMin = roundMinValue(min);
  const offset = (roundedMax - roundedMin) / ticks;
  const roundedOffset = roundOffset(offset);
  if (roundedMin < 0) {
    const negativeResults = [0];
    let negativeValue = 0;
    while (negativeValue > roundedMin) {
      negativeValue =
        negativeResults[negativeResults.length - 1] - roundedOffset;
      negativeResults.push(negativeValue);
    }
    let positiveValue = 0;
    const positiveResults = [0];
    while (positiveValue < roundedMax) {
      positiveValue =
        positiveResults[positiveResults.length - 1] + roundedOffset;
      positiveResults.push(positiveValue);
    }

    return [...new Set([...negativeResults.reverse(), ...positiveResults])];
  }
  const results = [];
  for (let i = 0; i <= ticks; i++) {
    results.push(roundedMin + i * roundedOffset);
  }
  return results;
};

const getOneDayTickValues = data => {
  let halfElevenTick = null;
  let thirteenTick = null;
  const fifteenTick = data.length - 1;
  const nineTick = 1;
  data.forEach((item, index) => {
    const date = new Date(item.tradingDate);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (!thirteenTick && hours === 13) {
      thirteenTick = index;
    }

    if (hours === 11) {
      if (minutes === 30) {
        halfElevenTick = index;
      }

      if (!halfElevenTick && minutes > 28 && minutes < 33) {
        halfElevenTick = index;
      }
    }
  });

  return [nineTick, halfElevenTick, thirteenTick, fifteenTick];
};

const getOneWeekTickValues = data => {
  const mapDayIndex = {};
  data.forEach((item, index) => {
    const date = moment(item.tradingDate);
    const dayOfWeek = date.format('ddd');
    const day = date.format('DD');
    if (mapDayIndex[`${dayOfWeek}_${day}`] !== undefined) {
      return;
    }
    mapDayIndex[`${dayOfWeek}_${day}`] = index;
  });

  return Object.values(mapDayIndex);
};

const getYearTickValues = data => {
  const mapYearIndex = {};
  data.forEach((item, index) => {
    const year = new Date(item.tradingDate).getFullYear();
    if (mapYearIndex[year] !== undefined) {
      return;
    }
    mapYearIndex[year] = index;
  });

  return Object.values(mapYearIndex);
};

const getXAxisFormatAndTicks = (data, timeRange) => {
  const length = data.length;
  switch (timeRange) {
    case Const.timeRange['1D']:
      return {
        format: 'HH:mm',
        tickValues: getOneDayTickValues(data),
        timeFormat: '%H:%M %m/%d/%Y',
      };
    case Const.timeRange['1W']:
      return {
        format: 'ddd',
        tickValues: getOneWeekTickValues(data),
        timeFormat: '%H:%M %m/%d/%Y',
      };
    case Const.timeRange['1M']:
    case Const.timeRange['3M']:
    case Const.timeRange['6M']:

    case Const.timeRange.YTD:
      return {
        format: 'MM/DD',
        tickValues: getTickValues(length, 5),
        timeFormat: '%H:%M %m/%d/%Y',
      };
    case Const.timeRange['3Y']:
    case Const.timeRange['5Y']:
    case Const.timeRange['1Y']:
      return {
        format: 'MMM',
        tickValues: getTickValues(length, 5),
        timeFormat: '%m/%d/%Y',
      };
    default:
      return null;
  }
};

const getOneDayTickFormat = data => {
  const tickValues = getOneDayTickValues(data);
  return d => {
    const index = tickValues.findIndex(tick => tick === d);
    const mapIndex = {
      0: '9:00',
      1: '11:30',
      2: '13:00',
      3: '15:00',
    };
    return mapIndex[index];
  };
};

const getTickFormat = (data, timeRange, isLoading) => {
  if (isLoading) {
    return () => '';
  }
  if (timeRange === Const.timeRange['1D']) {
    return getOneDayTickFormat(data);
  }

  return null;
};
